import type { LocalStore, TableName } from "@hq/local-store";

export type LiveQuerySnapshot<T> = {
  data: T | undefined;
  /** True until the first run resolves. */
  isLoading: boolean;
  error?: unknown;
};

export interface LiveQuery<T> {
  get(): T | undefined;
  /** A stable object that changes identity only when the result changes; safe for `useSyncExternalStore`. */
  snapshot(): LiveQuerySnapshot<T>;
  /** Fires after every completed run whose result is newer than the current one. */
  subscribe(listener: () => void): () => void;
  /** Run again now. Resolves once this run has been applied (or superseded). */
  refresh(): Promise<void>;
  dispose(): void;
}

/**
 * Runs `run` once, then again whenever one of `tables` commits. Runs that are superseded by a
 * newer run are dropped, so subscribers only ever see the latest result.
 */
export function createLiveQuery<T>(
  store: LocalStore,
  tables: readonly TableName[],
  run: (store: LocalStore) => Promise<T>,
): LiveQuery<T> {
  let snapshot: LiveQuerySnapshot<T> = { data: undefined, isLoading: true };
  let version = 0;
  let disposed = false;
  const listeners = new Set<() => void>();

  function publish(next: LiveQuerySnapshot<T>): void {
    snapshot = next;
    for (const l of [...listeners]) l();
  }

  async function refresh(): Promise<void> {
    if (disposed) return;
    const mine = ++version;
    try {
      const data = await run(store);
      if (mine !== version || disposed) return;
      publish({ data, isLoading: false });
    } catch (error) {
      if (mine !== version || disposed) return;
      publish({ data: snapshot.data, isLoading: false, error });
    }
  }

  const unsubscribe = store.subscribe(tables, () => void refresh());
  void refresh();

  return {
    get: () => snapshot.data,
    snapshot: () => snapshot,
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    refresh,
    dispose() {
      disposed = true;
      version++;
      unsubscribe();
      listeners.clear();
    },
  };
}
